import { BVHLoader } from '@cartwhl/3d/bvh';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';

import { editMotion, fetchBVHText, fetchMotion, saveMotion } from './api';
import type { Constraint, EndEffectorName } from './types';

type Deps = Record<string, never>;

type State = {
  motionID: string;
  apiKey: string;
  loading: boolean;
  error: string | null;
  bvhText: string | null;
  frameRate: number;
  totalFrames: number;
  currentFrame: number;
  isPlaying: boolean;
  playbackSpeed: number;
  constraints: Constraint[];
  selectedEffector: EndEffectorName | null;
  generating: boolean;
  editBvhURL: string | null;
  editRound: number;
  freezing: boolean;
  frozenMotionID: string | null;
  warning: string | null;
  showTrajectories: boolean;
};

const MIN_CONSTRAINT_FRAME_GAP = 4;

const initialState: State = {
  motionID: '',
  apiKey: 'service-api-key-01KFH1VD5F9DC2NTR2HX8QB8V3',
  loading: false,
  error: null,
  bvhText: null,
  frameRate: 30,
  totalFrames: 0,
  currentFrame: 0,
  isPlaying: false,
  playbackSpeed: 1,
  constraints: [],
  selectedEffector: null,
  generating: false,
  editBvhURL: null,
  editRound: 0,
  freezing: false,
  frozenMotionID: null,
  warning: null,
  showTrajectories: true,
};

class BmmPrototypeStore extends BaseStore<State, Deps> {
  private checkpointBvhText: string | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(deps: Deps) {
    super({ ...initialState, constraints: [] }, deps);
  }

  reset() {
    this.clearWarningTimer();
    this.checkpointBvhText = null;
    Object.assign(this.state, { ...initialState, constraints: [] });
  }

  destroy() {
    this.clearWarningTimer();
    super.destroy();
  }

  updateMotionID(motionID: string) {
    this.state.motionID = motionID;
  }

  updateApiKey(apiKey: string) {
    this.state.apiKey = apiKey;
  }

  setPlaybackSpeed(playbackSpeed: number) {
    this.state.playbackSpeed = playbackSpeed;
  }

  toggleTrajectories() {
    this.state.showTrajectories = !this.state.showTrajectories;
  }

  async loadMotion() {
    if (!this.state.motionID.trim()) {
      this.state.error = 'Please enter a motion ID';
      return;
    }

    this.state.loading = true;
    this.state.error = null;
    this.state.bvhText = null;
    this.state.constraints = [];
    this.state.currentFrame = 0;
    this.state.isPlaying = false;
    this.state.editBvhURL = null;
    this.state.editRound = 0;
    this.state.frozenMotionID = null;

    try {
      const motionData = await fetchMotion(
        this.state.motionID,
        this.state.apiKey,
      );

      if (motionData.status === 'FAILED') {
        throw new Error(motionData.errorMessage || 'Motion generation failed');
      }

      if (!motionData.bvhURL) {
        throw new Error(
          `No BVH URL available (status: ${motionData.status}). Motion may still be processing.`,
        );
      }

      const bvhText = await fetchBVHText(motionData.bvhURL);
      const { detectedFPS, totalFrames } = this.parseBvhTiming(bvhText);

      this.state.bvhText = bvhText;
      this.state.frameRate = detectedFPS;
      this.state.totalFrames = totalFrames;
      this.state.loading = false;
      this.checkpointBvhText = bvhText;
    } catch (error) {
      this.state.error = error instanceof Error ? error.message : String(error);
      this.state.loading = false;
    }
  }

  addConstraint(constraint: Constraint) {
    const existingConstraintIndex = this.state.constraints.findIndex(
      (c) =>
        c.frame === constraint.frame &&
        c.endEffector === constraint.endEffector,
    );
    if (existingConstraintIndex >= 0) {
      this.state.constraints[existingConstraintIndex] = constraint;
      return;
    }

    // Allow edits on existing keyframes (frames that already have a constraint)
    const isExistingKeyframe = this.state.constraints.some(
      (c) => c.frame === constraint.frame,
    );
    if (!isExistingKeyframe) {
      const tooCloseConstraint = this.state.constraints.find(
        (c) => Math.abs(c.frame - constraint.frame) < MIN_CONSTRAINT_FRAME_GAP,
      );
      if (tooCloseConstraint) {
        this.showWarning(
          `Frame ${constraint.frame} is too close to existing ${tooCloseConstraint.endEffector.replace(/_/g, ' ')} constraint at frame ${tooCloseConstraint.frame}. ` +
            `Please use a frame at least ${MIN_CONSTRAINT_FRAME_GAP} frames apart.`,
        );
        return;
      }
    }

    this.state.constraints.push(constraint);
  }

  removeConstraint(index: number) {
    this.state.constraints.splice(index, 1);
  }

  clearConstraints() {
    this.state.constraints = [];
  }

  setFrame(frame: number) {
    this.state.currentFrame = Math.max(
      0,
      Math.min(frame, this.state.totalFrames - 1),
    );
  }

  togglePlay() {
    this.state.isPlaying = !this.state.isPlaying;
  }

  setPlaying(playing: boolean) {
    this.state.isPlaying = playing;
  }

  selectEffector(name: EndEffectorName | null) {
    this.state.selectedEffector = name;
  }

  async regenerate() {
    if (this.state.constraints.length === 0) {
      this.state.error = 'No constraints to apply';
      return;
    }

    this.state.generating = true;
    this.state.error = null;

    try {
      const constraintStr = this.state.constraints
        .map(
          (c) =>
            `${c.frame} ${c.endEffector} ${c.dx.toFixed(4)} ${c.dy.toFixed(4)} ${c.dz.toFixed(4)}`,
        )
        .join('\n');

      const result = await editMotion(
        this.state.motionID,
        constraintStr,
        this.state.apiKey,
      );
      const bvhText = await fetchBVHText(result.bvhURL);
      const { detectedFPS, totalFrames } = this.parseBvhTiming(bvhText);

      this.state.bvhText = bvhText;
      this.state.frameRate = detectedFPS;
      this.state.totalFrames = totalFrames;
      this.state.editBvhURL = result.bvhURL;
      this.state.editRound += 1;
      this.state.generating = false;
    } catch (error) {
      this.state.error = error instanceof Error ? error.message : String(error);
      this.state.generating = false;
    }
  }

  async freeze() {
    if (!this.state.editBvhURL) {
      this.state.error = 'No edited motion to freeze - regenerate first';
      return;
    }

    this.state.freezing = true;
    this.state.error = null;
    this.state.frozenMotionID = null;

    try {
      const result = await saveMotion(
        this.state.editBvhURL,
        `Edit of ${this.state.motionID}`,
        this.state.apiKey,
      );

      this.state.frozenMotionID = result.motionID;
      this.state.constraints = [];
      this.state.editBvhURL = null;
      this.state.editRound = 0;
      this.state.freezing = false;
      this.checkpointBvhText = this.state.bvhText;
    } catch (error) {
      this.state.error = error instanceof Error ? error.message : String(error);
      this.state.freezing = false;
    }
  }

  undo() {
    if (!this.checkpointBvhText) {
      this.state.error = 'No checkpoint to undo to';
      return;
    }
    if (this.state.editRound === 0) {
      this.state.error = 'Nothing to undo';
      return;
    }

    this.state.bvhText = this.checkpointBvhText;
    this.state.constraints = [];
    this.state.editBvhURL = null;
    this.state.editRound = 0;
    this.state.error = null;
  }

  getSourceBvhText() {
    return this.checkpointBvhText;
  }

  private showWarning(message: string) {
    this.state.warning = message;
    this.clearWarningTimer();
    this.warningTimer = setTimeout(() => {
      this.state.warning = null;
      this.warningTimer = null;
    }, 4000);
  }

  private clearWarningTimer() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private parseBvhTiming(bvhText: string): {
    detectedFPS: number;
    totalFrames: number;
  } {
    const loader = new BVHLoader();
    const bvh = loader.parse(bvhText);
    const duration = bvh.clip.duration;

    let detectedFPS = 30;
    if (bvh.clip.tracks.length > 0) {
      const times = bvh.clip.tracks[0].times;
      if (times.length >= 2) {
        detectedFPS = Math.round(1 / (times[1] - times[0]));
      }
    }

    return {
      detectedFPS,
      totalFrames: Math.floor(duration * detectedFPS),
    };
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: BmmPrototypeStore,
  displayName: 'BmmPrototypeStore',
  staticDeps: {},
});

export type { BmmPrototypeStore };
